import {BehaviorSubject, Observable} from "rxjs";
import {finalize, scan, take} from "rxjs/operators";
import {DestroyRef, inject, Injectable} from "@angular/core";
import {takeUntilDestroyed} from "@angular/core/rxjs-interop";
import {deepCopy} from "../../lib/utils";

@Injectable()
/**
 **This service is stateful.
 **/

export class SelectSearchApiService {
    private readonly destroyRef = inject(DestroyRef);

    constructor() {
    }

    loadNext: boolean = true;
    loadNextSelected: boolean = true;
    loadingAvailable: boolean = true;
    loadingSelected: boolean = true;
    page_number: number = 1;
    page_number_selected: number = 1;
    page_size: number = 10;
    options_available: BehaviorSubject<any[]> = new BehaviorSubject<any[]>([]);
    options_selected: BehaviorSubject<any[]> = new BehaviorSubject<any[]>([]);
    clear: boolean = false;
    clear_selected: boolean = false;
    keep_selected: boolean = true;


    itemsAdded: BehaviorSubject<any[]> = new BehaviorSubject<any[]>([]);
    itemsRemoved: BehaviorSubject<any[]> = new BehaviorSubject<any[]>([]);

    availableTotal: number = 0;
    selectedTotal: number = 0;

    // TODO Note: just adding full scroll height is not enough as user can then scroll very quickly and be outside the
    // bounds of the next page. Needs full positional indexing
    item_height: number = 46;
    pages: number = 1;

    /**Whether to include (filter) the selected list when typing in the search box**/

    mapFunction(curr) {
        if (!curr) {
            return;
        }
        return curr.map(item => ({
            text: this.stringFunction(item),
            value: this.valueFunction(item),
        }));
    }

    stringFunction(obj: any): string {
        if (!obj) {
            return;
        }
        let name_: string = '';
        let descrip_: string = '';

        if (obj.attributes && obj.attributes.name !== undefined) {
            name_ = obj.attributes.name;
        }

        if (obj.attributes?.description) {
            descrip_ = '-' + obj.attributes.description;
        }
        return name_ + descrip_;
    }

    valueFunction(obj: any): any {
        return obj;
    }

    connect(mapFunction = this.mapFunction.bind(this)): Observable<any[]> {
        return this.options_available.asObservable().pipe(
            scan((acc: any, curr: any[]) => {
                let list: any[] = [];
                const curr_option$ = mapFunction(curr);
                if (!this.clear) {
                    list = this.filterAvailable([...acc, ...curr_option$]);
                } else {
                    this.clear = false;
                    list = [...curr_option$];
                }
                return this.dedupeByValue(list);
            }, []),
            takeUntilDestroyed(this.destroyRef)
        );
    }

    connect_selected(mapFunction = this.mapFunction.bind(this)) {
        return this.options_selected.asObservable().pipe(
            scan((acc: any, curr: any[]) => {
                let list = [];
                const curr_option$ = mapFunction(curr);
                if (!this.clear_selected) {
                    list = this.filterSelected([...acc, ...curr_option$]);
                } else {
                    this.clear_selected = false;
                    list = [...curr_option$];
                }
                return this.dedupeByValue(list);
            }, []),
            takeUntilDestroyed(this.destroyRef)
        );
    }

    filterAvailable(curr_list: any[]): any[] {
        const added: any[] = this.itemsAdded.getValue();
        const removed: any[] = this.itemsRemoved.getValue();

        if (added?.length) {
            const addedKeys = new Set(added.map(a => a.id || a));
            curr_list = curr_list.filter(i => !addedKeys.has(i.value?.id || i.id || i.value || i));
        }
        if (removed?.length) {
            const items_removed = this.mapFunction(removed);
            curr_list.push(...items_removed);
        }
        return curr_list;
    }

    filterSelected(curr_list: any[]): any[] {
        const added: any[] = this.itemsAdded.getValue();
        const removed: any[] = this.itemsRemoved.getValue();

        if (removed?.length) {
            const removedKeys = new Set(removed.map(r => r.id || r));
            curr_list = curr_list.filter(i => !removedKeys.has(i.value?.id || i.id || i.value || i));
        }
        if (added?.length) {
            const items_added = this.mapFunction(added);
            curr_list.push(...items_added);
        }
        return curr_list;
    }

    /**
     * For getting the compare property/object
     */
    private keyOfItem(item: any): unknown {
        if (item == null || typeof item !== 'object') {
            return item;
        }
        const v = item.value;
        if (v != null && typeof v === 'object' && v.id != null) {
            return v.id;
        }
        if (v !== undefined) {
            return v;
        }
        if (item.id != null) {
            return item.id;
        }
        return item.text ?? item;
    }

    private dedupeByValue(list: {text: string, value: any}[]): {text: string, value: any}[] {
        const seen = new Set<unknown>();
        const out: {text: string, value: any}[] = [];
        for (const item of list) {
            const key = this.keyOfItem(item);
            if (!seen.has(key)) {
                seen.add(key);
                out.push(item);
            }
        }
        return out;
    }

    resetList(init_value: any[] = []): void {
        this.page_number = 1;
        this.clear = true;
        this.options_available.next(init_value);
        this.loadNext = true;
    }

    resetSelectedList(): void {
        this.page_number_selected = 1;
        this.clear_selected = true;
        this.options_selected.next([]);
        this.loadNextSelected = true;
    }

    getNextBatch(fetchData, filterValue, page: number = this.page_number, items_added?, items_removed?): void {
        this.loadingAvailable = true;

        fetchData(page, filterValue, items_added, items_removed).pipe(
            take(1),
            finalize(() => this.loadingAvailable = false),
        ).subscribe({
            next: (data): void => {
                this.page_number += 1;
                this.options_available.next(data.data);
                if (!data.data.length) {
                    this.loadNext = false;
                }
                this.availableTotal = data.meta?.count || 0;
            },
            error: (err) => console.error('[SelectSearchApiService] failed to load available options:', err),
        });
    }

    getNextSelected(fetchSelected, filterValue, page: number = this.page_number_selected, items_added?, items_removed?, filterByAccount: boolean = true) {
        if (this.keep_selected) {
            filterValue = null;
            filterByAccount = false;
        }

        this.loadingSelected = true;

        fetchSelected(page, filterValue, items_added, items_removed, filterByAccount).pipe(
            take(1),
            finalize(() => this.loadingSelected = false),
        ).subscribe({
            next: (data) => {
                this.page_number_selected += 1;
                this.options_selected.next(data.data);
                if (!data.data.length) {
                    this.loadNextSelected = false;
                }
                this.selectedTotal = data.meta?.count || 0;
            },
            error: (err) => console.error('[SelectSearchApiService] failed to load selected options:', err),
        });
    }

}
